import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IoMdArrowDropdown } from 'react-icons/io';

export default function ColumnCategoryFilterHeader({
  label,
  options = [],
  selectedValues = [],
  onChange,
  searchable = false,
  variant = 'dark',
  searchPlaceholder = 'Search...',
  emptyMessage = 'No options found.',
  onOpenChange,
}) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef(null);
  const isActive = selectedValues.length > 0;

  const closeMenu = useCallback(() => {
    setOpen(false);
    setSearchQuery('');
    onOpenChange?.(false);
  }, [onOpenChange]);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        closeMenu();
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeMenu();
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, closeMenu]);

  const filteredOptions = useMemo(() => {
    if (!searchable || !searchQuery.trim()) {
      return options;
    }
    const query = searchQuery.trim().toLowerCase();
    return options.filter((option) => option.label.toLowerCase().includes(query));
  }, [options, searchable, searchQuery]);

  const handleToggle = (event) => {
    event.stopPropagation();
    setOpen((prev) => {
      const next = !prev;
      if (prev) {
        setSearchQuery('');
      }
      onOpenChange?.(next);
      return next;
    });
  };

  const handleOptionToggle = (value) => {
    if (selectedValues.includes(value)) {
      onChange(selectedValues.filter((item) => item !== value));
      return;
    }
    onChange([...selectedValues, value]);
  };

  const headerTextClass = variant === 'dark' ? 'text-white' : 'text-gray-900';
  const chevronClass = variant === 'dark' ? 'text-white' : 'text-gray-700';

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={handleToggle}
        className={`flex items-center gap-1 whitespace-nowrap font-semibold ${headerTextClass}`}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span>{label}</span>
        {isActive && (
          <span className="inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-white/20 px-1 text-[10px] font-bold">
            {selectedValues.length}
          </span>
        )}
        <IoMdArrowDropdown className={`text-base shrink-0 ${chevronClass}`} aria-hidden />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute left-0 top-full z-30 mt-1 min-w-[14rem] max-w-[18rem] rounded-md border border-gray-200 bg-white py-1 shadow-lg"
        >
          {searchable && (
            <div className="border-b border-gray-100 px-2 py-2">
              <input
                type="text"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder={searchPlaceholder}
                className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm text-gray-800 focus:border-[#674936] focus:outline-none"
                onClick={(event) => event.stopPropagation()}
              />
            </div>
          )}

          <div className="max-h-64 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <p className="px-3 py-2 text-sm text-gray-500">{emptyMessage}</p>
            ) : (
              filteredOptions.map((option) => {
                const selected = selectedValues.includes(option.value);
                return (
                  <label
                    key={option.value}
                    role="menuitemcheckbox"
                    aria-checked={selected}
                    className={`flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-100 ${
                      selected ? 'bg-gray-50' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => handleOptionToggle(option.value)}
                      className="h-4 w-4 shrink-0 rounded border-gray-300 text-[#674936] focus:ring-[#674936]"
                    />
                    <span className="break-words">{option.label}</span>
                  </label>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
